import { useCallback } from 'react'
import { create } from 'zustand'
import { useQueryClient } from '@tanstack/react-query'

import { WebsiteRead } from '@/api/dashboard'
import { themeQueryOpts } from '../api'
import { TransformResult, transformTheme } from '../libs/theme'

const transformedThemeStore = create<{
  loading: boolean
  result: TransformResult | null
  setLoading: (loading: boolean) => void
  setResult: (result: TransformResult | null) => void
}>(set => ({
  loading: false,
  result: null,
  setLoading: loading => set({ loading }),
  setResult: result => set({ result }),
}))

/**
 * This hook is used to fetch & transform theme.
 *
 * - Does not recalculate tokens when the hook is called.
 * - To recalculate tokens, you need to call the `transform` function.
 *
 * @param gameId
 * @param website
 * @returns
 */
export const useTransformedTheme = (gameId: string, website: WebsiteRead) => {
  const ql = useQueryClient()
  const theme = ql.getQueryData(themeQueryOpts(gameId, website).queryKey)

  const { loading, result, setLoading, setResult } = transformedThemeStore()

  const transform = useCallback(async () => {
    if (!theme) {
      return
    }
    setLoading(true)
    try {
      await new Promise(resolve => setTimeout(resolve, 200))
      const transformed = await transformTheme(theme)
      console.log(transformed)

      setResult(transformed)
    } finally {
      setLoading(false)
    }
  }, [theme, setResult, setLoading])

  return { data: result, loading, transform }
}
